import React, { useEffect, useState, useMemo } from 'react';
import SEO from '../components/SEO';
import usePage from '../hooks/usePage';
import API, { UPLOADS_URL } from '../api/axios';
import Lightbox from '../components/ui/Lightbox';
import Reveal from '../components/ui/Reveal';

const DEFAULT_CONTENT = `
<p class="text-lg md:text-xl text-[#1B3260] font-medium italic mb-6 leading-relaxed">&ldquo;St. Andrews World School has been accredited with the <strong>&lsquo;Excellence in Modern Education in Schooling&rsquo;</strong> Award, which marks the excellence of our modern curriculum.&rdquo;</p>
<p class="mb-4">St. Andrews World School offers the best education that is centered on the ICSE curriculum. The curriculum is carefully designed to encourage studies through experiential and project-based learning. St. Andrews World School has recently won the <strong><em>Excellence in Modern Education</em></strong> award. This award highlights how we are working towards revolutionizing schooling in India. Our vision is to be a leader in academic excellence that prepares students to be successful in all aspects of life.</p>
<p>We have collaborated with <strong>Cambridge Assessment International education organization</strong>, to design the assessments which help the school to meet and maintain the global standards of a certification program. St. Andrews World School offers Cambridge Assessments which are in-depth exams that make learning effective, fun, and rewarding journey. The assessment offered is unique and it promotes continuous progression with a directed path to improve English language skills that are at an international level.</p>`;

const resolveImg = (src) => {
  if (!src) return '';
  if (src.startsWith('/frontend/')) return src.replace('/frontend/', '/');
  if (src.startsWith('http') || src.startsWith('/')) return src;
  if (src.startsWith('awards/')) {
    return `https://sawsindirapuram.com/admin/public/uploads/${src}`;
  }
  return `${UPLOADS_URL}/${src}`;
};

const FALLBACK_AWARDS = [
  {
    id: 1,
    name: 'Excellence in Modern Education in Schooling Award',
    image: `${process.env.PUBLIC_URL}/images/awards/1.jpg.jpeg`,
    category: { name: 'School Awards' },
    awarded_by: 'National Education Leadership Summit',
    year: '2025',
    desc: 'Honored for pioneering excellence in child-centric experiential learning and ICSE curriculum design.'
  },
  {
    id: 2,
    name: 'Best Campus Infrastructure & Holistic Learning',
    image: `${process.env.PUBLIC_URL}/images/awards/2.jpg.jpeg`,
    category: { name: 'School Awards' },
    awarded_by: 'Eminent Education Conclave',
    year: '2025',
    desc: 'Recognized for state-of-the-art campus infrastructure and well-rounded academic and sports facilities.'
  },
  {
    id: 3,
    name: 'Leadership & Quality Excellence in Education',
    image: `${process.env.PUBLIC_URL}/images/awards/3.jpg.jpeg`,
    category: { name: 'Leadership Recognitions' },
    awarded_by: 'K12 School Leadership Forum',
    year: '2024',
    desc: 'Awarded for exceptional academic leadership, faculty excellence, and student empowerment.'
  },
  {
    id: 4,
    name: 'Top Emerging ICSE School in Ghaziabad',
    image: `${process.env.PUBLIC_URL}/images/awards/4.jpg.jpeg`,
    category: { name: 'Academic Honours' },
    awarded_by: 'School Achievement Platform',
    year: '2024',
    desc: 'Recognizing remarkable academic performance, progressive teaching, and holistic development.'
  },
  {
    id: 5,
    name: 'Best Co-Curricular & Sports Learning School',
    image: `${process.env.PUBLIC_URL}/images/awards/5.jpg.jpeg`,
    category: { name: 'Student Excellence' },
    awarded_by: 'Global School Sports Federation',
    year: '2024',
    desc: 'Celebrating top student accomplishments in athletics, arts, and competitive extracurricular activities.'
  },
  {
    id: 6,
    name: 'Distinguished School of the Year Award',
    image: `${process.env.PUBLIC_URL}/images/awards/6.webp`,
    category: { name: 'School Awards' },
    awarded_by: 'Asia Education Icon Awards',
    year: '2023',
    desc: 'Conferred for benchmarking modern school education standards and nurturing future-ready global leaders.'
  },
];

const DEFAULT_SECTIONS = [
  { section_key: 'hero', section_type: 'hero', title: 'Awards & Recognition', image: '/images/page-banners/why-choose-us.jpg' },
  {
    section_key: 'intro',
    section_type: 'intro',
    kicker: 'Hall of Fame',
    title: 'Our Awards & Recognitions',
    description: 'Celebrating our milestones of academic innovation, visionary leadership, and student accomplishments.',
  },
];

const Recognition = () => {
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const pageData = usePage('recognition', { content: DEFAULT_CONTENT });
  const [awards, setAwards] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxItems, setLightboxItems] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    let mounted = true;

    const fetchData = async () => {
      try {
        const [secRes, awdRes] = await Promise.allSettled([
          API.get('/recognition/sections'),
          API.get('/awards/public'),
        ]);

        if (!mounted) return;

        if (secRes.status === 'fulfilled' && secRes.value.data?.data?.length) {
          setSections(secRes.value.data.data);
        }

        if (awdRes.status === 'fulfilled' && awdRes.value.data?.data?.length) {
          setAwards(awdRes.value.data.data);
        } else {
          setAwards(FALLBACK_AWARDS);
        }
      } catch (err) {
        if (mounted) setAwards(FALLBACK_AWARDS);
      }
    };

    fetchData();
    return () => { mounted = false; };
  }, []);

  const openLightbox = (items, index = 0) => {
    const formatted = items.map(a => ({ img: resolveImg(a.image), alt: a.name || 'Award' }));
    setLightboxItems(formatted);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const heroSec = sections.find((s) => s.section_type === 'hero');
  const introSec = sections.find((s) => s.section_type === 'intro') || DEFAULT_SECTIONS.find((s) => s.section_type === 'intro');

  const pageTitle = heroSec?.title || 'Awards & Recognition';
  const bannerImg = heroSec?.image
    ? resolveImg(heroSec.image)
    : (pageData.bannerImage || '/images/page-banners/why-choose-us.jpg');

  return (
    <>
      <SEO
        slug="recognition"
        title={`${pageTitle} | St. Andrews World School, Indirapuram`}
        description="Explore the prestigious awards, recognitions, and accreditations earned by St. Andrews World School, Indirapuram for excellence in ICSE curriculum and holistic education."
        keywords="Awards, Recognition, Accreditations, SAWS Indirapuram, St Andrews World School Indirapuram, Best School in Ghaziabad"
      />

      {/* ─── HERO BANNER ─────────────────────────────────────────── */}
      <section className="page-hero">
        <img
          src={bannerImg}
          alt={pageTitle}
          className="page-hero-img"
          loading="eager"
          onError={(e) => {
            if (!e.currentTarget.dataset.fallbackTried) {
              e.currentTarget.dataset.fallbackTried = '1';
              e.currentTarget.src = '/images/page-banners/why-choose-us.jpg';
            }
          }}
        />
        <div className="page-hero-content">
          <div className="container">
            <div className="page-hero-inner">
            </div>
          </div>
        </div>
      </section>

      {/* ─── AWARDS & RECOGNITIONS GALLERY ───────────────────────── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container max-w-[1310px] mx-auto px-4">
          
          {/* Section Header */}
          {introSec && (
            <Reveal>
              <div className="text-center max-w-2xl mx-auto mb-10">
                {introSec.kicker && (
                  <div className="kicker text-[#D4A73E] font-bold uppercase tracking-wider text-xs md:text-sm">
                    {introSec.kicker}
                  </div>
                )}
                <h2 className="text-2xl md:text-4xl font-bold text-[#1B3260] mt-1.5 leading-tight">
                  {introSec.title || 'Our Awards & Recognitions'}
                </h2>
                <div className="w-14 h-[3px] bg-[#D4A73E] rounded-full mx-auto my-4"></div>
                {introSec.description && (
                  <div className="text-gray-600 text-sm md:text-base leading-relaxed space-y-3">
                    {introSec.description.split(/\n\s*\n/).map((p, i) => (
                      <p key={i}>{p.trim()}</p>
                    ))}
                  </div>
                )}
              </div>
            </Reveal>
          )}

          {/* Awards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-7">
            {awards.map((award, idx) => (
              <Reveal key={award.id || idx} delayMs={idx * 40} className="h-full">
                <button
                  type="button"
                  onClick={() => openLightbox(awards, idx)}
                  className="group w-full text-left bg-white rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 border border-gray-100 overflow-hidden flex flex-col h-full cursor-pointer"
                >
                  {/* Card Image */}
                  <div className="relative aspect-[4/3] bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-5 overflow-hidden border-b border-gray-100">
                    <img
                      src={resolveImg(award.image)}
                      alt={award.name || 'Award'}
                      className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform duration-500 drop-shadow-md"
                      loading="lazy"
                      onError={(e) => {
                        if (!e.currentTarget.dataset.fallbackTried && award.image) {
                          e.currentTarget.dataset.fallbackTried = '1';
                          const cur = e.currentTarget.src;
                          if (cur.includes('/admin/uploads/')) {
                            e.currentTarget.src = cur.replace('/admin/uploads/', '/admin/public/uploads/');
                          } else if (cur.includes('/admin/public/uploads/')) {
                            e.currentTarget.src = cur.replace('/admin/public/uploads/', '/admin/uploads/');
                          }
                        }
                      }}
                    />
                    <div className="absolute inset-0 bg-[#1B3260]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-white text-[#1B3260] flex items-center justify-center opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 shadow-md">
                        <i className="fas fa-search-plus"></i>
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-5 flex-1 flex flex-col justify-between">
                    <div>
                      {/* Category Badge */}
                      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-[#1B3260]/5 text-[#1B3260] mb-3">
                        <i className="fas fa-award text-[#D4A73E]"></i>
                        {award.category?.name || 'School Awards'}
                      </div>

                      <h3 className="font-bold text-[#1B3260] text-base md:text-lg leading-snug group-hover:text-[#D4A73E] transition-colors line-clamp-2">
                        {award.name}
                      </h3>

                      {award.awarded_by && (
                        <p className="text-xs text-gray-500 mt-2 flex items-center gap-1.5 line-clamp-1">
                          <i className="fas fa-building text-gray-400"></i> {award.awarded_by}
                        </p>
                      )}
                    </div>

                    {/* Card Footer (Year) */}
                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs">
                      {award.year ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#D4A73E]/10 text-[#B88924] font-bold">
                          <i className="far fa-calendar-alt"></i> {award.year}
                        </span>
                      ) : (
                        <span className="text-gray-400 font-medium">Recognized Honour</span>
                      )}
                      <span className="text-[#1B3260] font-semibold text-xs group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                        View <i className="fas fa-arrow-right text-[10px]"></i>
                      </span>
                    </div>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>

        </div>
      </section>

      {/* ─── LIGHTBOX MODAL ──────────────────────────────────────── */}
      <Lightbox
        open={lightboxOpen}
        items={lightboxItems}
        initialIndex={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
};

export default Recognition;
